import React, { useState } from 'react'
import { toast } from 'components/ui/toast-store'
import { Button } from 'components/ui/button'
import { Alert, AlertDescription } from 'components/ui/alert'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'components/ui/dialog'
import { FileUp } from 'lucide-react'
import { useFileImport } from '../hooks'
import type { ImportModalProps, ImportOptions } from '../types/financials.types'
import { formatFileSize } from '../utils/formatting'
import { parseFile, convertImportToIncomeStatement } from '../utils/fileParser'

export const ImportModal: React.FC<ImportModalProps> = ({ isOpen, onClose, onImport, existingQuarters = [] }) => {
	const { isImporting, importProgress, importError, preview, uploadFile, confirmImport, cancelImport } =
		useFileImport()

	const [dragOver, setDragOver] = useState(false)
	const [importMode, setImportMode] = useState<'merge' | 'replace'>('merge')
	const [uploadedFile, setUploadedFile] = useState<File | null>(null)
	const [rawFileData, setRawFileData] = useState<any[][] | null>(null)
	// Track custom formulas for each line item
	const [customFormulas, setCustomFormulas] = useState<Record<string, string>>({})
	// Track which field is being edited
	const [editingField, setEditingField] = useState<string | null>(null)
	const [formulaInput, setFormulaInput] = useState('')

	// Reset formulas when modal closes
	React.useEffect(() => {
		if (!isOpen) {
			setCustomFormulas({})
			setEditingField(null)
			setFormulaInput('')
		}
	}, [isOpen])

	const handleFileSelect = async (file: File) => {
		try {
			console.log('=== FILE SELECTED ===')
			console.log('File name:', file.name)
			console.log('File size:', file.size)
			console.log('File type:', file.type)

			// Reset formulas when new file is selected
			setCustomFormulas({})
			setEditingField(null)
			setFormulaInput('')

			const result = await uploadFile(file)
			console.log('Upload result:', result)
		} catch (error) {
			// Error is already set in the hook
			console.error('=== UPLOAD ERROR ===')
			console.error('Upload error:', error)
			toast.error(`Failed to parse file: ${error instanceof Error ? error.message : 'Unknown error'}`)
		}
	}

	const handleFileDrop = (e: React.DragEvent) => {
		e.preventDefault()
		setDragOver(false)

		const files = e.dataTransfer.files
		if (files.length > 0) {
			handleFileSelect(files[0])
		}
	}

	const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files
		if (files && files.length > 0) {
			handleFileSelect(files[0])
		}
	}

	const handleStartEditFormula = (lineItemId: string) => {
		setEditingField(lineItemId)
		setFormulaInput(customFormulas[lineItemId] || '')
	}

	const handleSaveFormula = (lineItemId: string) => {
		if (formulaInput.trim()) {
			setCustomFormulas(prev => ({
				...prev,
				[lineItemId]: formulaInput.trim()
			}))
		} else {
			// Remove formula if input is empty
			setCustomFormulas(prev => {
				const newFormulas = { ...prev }
				delete newFormulas[lineItemId]
				return newFormulas
			})
		}
		setEditingField(null)
		setFormulaInput('')
	}

	const handleCancelEditFormula = () => {
		setEditingField(null)
		setFormulaInput('')
	}

	// Helper function to convert user-friendly formula to backend format
	const convertFormulaToBackendFormat = (formula: string): string => {
		// Map of display names to line item IDs
		const nameToIdMap: Record<string, string> = {
			'sales': 'sales',
			'revenue': 'sales',
			'cogs': 'cogs',
			'cost of goods sold': 'cogs',
			'gross profit': 'gross_profit',
			'gross margin': 'gross_profit',
			'expenses': 'expenses',
			'operating expenses': 'expenses',
			'earnings': 'earnings',
			'net income': 'earnings',
			'net profit': 'earnings'
		}

		let convertedFormula = formula.toLowerCase()

		// Replace display names with IDs (sort by length to handle longer matches first)
		const sortedNames = Object.keys(nameToIdMap).sort((a, b) => b.length - a.length)
		sortedNames.forEach(name => {
			const id = nameToIdMap[name]
			// Use word boundaries to match whole words only
			const regex = new RegExp(`\\b${name}\\b`, 'gi')
			convertedFormula = convertedFormula.replace(regex, id)
		})

		return convertedFormula.trim()
	}

	const handleConfirmImport = async () => {
		if (!preview || !preview.rawData) return

		const options: ImportOptions = {
			mode: importMode,
			mappings: preview.mappings,
		}

		try {
			// Convert raw data to line items and quarters
			const importedData = convertImportToIncomeStatement(
				preview.rawData,
				preview.mappings,
				existingQuarters
			)

			console.log('=== IMPORT MODAL: CONVERTED DATA ===')
			console.log('Line items:', importedData.lineItems.map(li => ({
				id: li.id,
				label: li.label,
				type: li.type,
				valueCount: li.values?.length,
				sampleValue: li.values?.[0]
			})))
			console.log('Quarters:', importedData.quarters)
			console.log('Custom formulas active:', customFormulas)

			// Apply custom formulas to line items - convert them to calculated fields
			importedData.lineItems = importedData.lineItems.map(item => {
				const formula = customFormulas[item.id || '']
				if (formula) {
					// Convert user-friendly formula to backend format
					const backendFormula = convertFormulaToBackendFormat(formula)
					console.log(`⚠️ Converting ${item.id} to calculated field: "${formula}" → "${backendFormula}"`)

					return {
						...item,
						type: 'calculated',
						formula: backendFormula, // Store in backend format (e.g., "gross_profit - expenses")
						values: (item.values || []).map(v => ({ ...v, value: 0 })) // Will be calculated by backend
					}
				}
				return item
			})

			console.log('=== AFTER FORMULA APPLICATION ===')
			console.log('Line items:', importedData.lineItems.map(li => ({
				id: li.id,
				type: li.type,
				formula: li.formula,
				valueCount: li.values?.length,
				sampleValue: li.values?.[0]
			})))

			await confirmImport(preview, options, async data => {
				// Pass the converted data to the parent component
				console.log('ImportModal: Calling onImport with:', {
					fileName: data.preview.fileName,
					options: data.options,
					importedData
				})
				await onImport(data.preview.fileName, data.options, importedData)

				// Reset formulas after successful import
				setCustomFormulas({})
				setEditingField(null)
				setFormulaInput('')

				// Wait a bit for state to update before closing
				await new Promise(resolve => setTimeout(resolve, 500))
				onClose()
			})
		} catch (error) {
			console.error('Import error:', error)
			// Show error to user
			toast.error(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
		}
	}

	const handleCancel = () => {
		// Reset formulas when canceling
		setCustomFormulas({})
		setEditingField(null)
		setFormulaInput('')

		cancelImport()
		onClose()
	}

	return (
		<Dialog open={isOpen} onClose={handleCancel}>
			<DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
							<FileUp className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>Import Income Statement</DialogTitle>
							<DialogDescription>Upload a file to import financial data</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="flex-1 overflow-y-auto px-5 py-4">
					{!preview ? (
						// File Upload View
						<div>
							{importError && (
								<Alert variant="danger" className="mb-4">
									<AlertDescription>{importError.message}</AlertDescription>
								</Alert>
							)}

							<div
								className={`
									border-2 border-dashed rounded-lg p-12 text-center
									transition-colors
									${dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
									${isImporting ? 'pointer-events-none opacity-50' : ''}
								`}
								onDragOver={e => {
									e.preventDefault()
									setDragOver(true)
								}}
								onDragLeave={() => setDragOver(false)}
								onDrop={handleFileDrop}
							>
								<svg
									className="mx-auto h-12 w-12 text-gray-400"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
									/>
								</svg>

								<p className="mt-4 text-lg font-medium text-gray-900">Drag & Drop File Here</p>
								<p className="mt-1 text-sm text-gray-500">or</p>

								<label className="mt-4 inline-block">
									<input
										type="file"
										accept=".xlsx,.xls,.csv"
										onChange={handleFileInput}
										className="hidden"
										disabled={isImporting}
									/>
									<span className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
										Browse Files...
									</span>
								</label>

								<p className="mt-4 text-xs text-gray-500">
									Supported formats: .xlsx, .csv
									<br />
									Maximum file size: 10 MB
								</p>
							</div>

							{isImporting && importProgress > 0 && (
								<div className="mt-4">
									<div className="flex justify-between text-sm text-gray-600 mb-1">
										<span>Processing file...</span>
										<span>{importProgress}%</span>
									</div>
									<div className="w-full bg-gray-200 rounded-full h-2">
										<div
											className="bg-blue-600 h-2 rounded-full transition-all duration-300"
											style={{ width: `${importProgress}%` }}
										/>
									</div>
								</div>
							)}

							<div className="mt-6 p-4 bg-gray-50 rounded-lg">
								<h4 className="text-sm font-medium text-gray-900 mb-2">📄 Sample Template</h4>
								<p className="text-sm text-gray-600 mb-2">
									Not sure about the file format? Download our sample template to get started.
								</p>
								<Button variant="ghost">
									Download sample template.xlsx
								</Button>
							</div>
						</div>
					) : (
						// Import Preview View
						<div>
							<div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
								<p className="text-sm text-green-800">
									✅ File successfully parsed! Found {preview.lineItemsFound} line items and {preview.quartersFound}{' '}
									quarters.
								</p>
							</div>

							{preview.warnings.length > 0 && (
								<div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
									<ul className="list-disc list-inside text-sm text-yellow-800">
										{preview.warnings.map((warning, index) => (
											<li key={index}>{warning}</li>
										))}
									</ul>
								</div>
							)}

							<div className="mb-4">
								<label className="block text-sm font-medium text-gray-700 mb-2">Import Options:</label>
								<div className="space-y-2">
									<label className="flex items-center">
										<input
											type="radio"
											value="merge"
											checked={importMode === 'merge'}
											onChange={e => setImportMode(e.target.value as 'merge' | 'replace')}
											className="h-4 w-4 text-blue-600 focus:ring-blue-500"
										/>
										<span className="ml-2 text-sm text-gray-700">
											<strong>Merge with existing data</strong> (update matching line items, keep existing)
										</span>
									</label>
									<label className="flex items-center">
										<input
											type="radio"
											value="replace"
											checked={importMode === 'replace'}
											onChange={e => setImportMode(e.target.value as 'merge' | 'replace')}
											className="h-4 w-4 text-blue-600 focus:ring-blue-500"
										/>
										<span className="ml-2 text-sm text-gray-700">
											<strong>Replace all data</strong> (clear existing and import fresh)
										</span>
									</label>
								</div>
							</div>

							<div className="border border-gray-200 rounded-lg overflow-hidden">
								<div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
									<h3 className="text-sm font-medium text-gray-900">Line Item Mapping</h3>
								</div>
								<div className="max-h-64 overflow-y-auto">
									<table className="min-w-full divide-y divide-gray-200">
										<thead className="bg-gray-50">
											<tr>
												<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
													From File
												</th>
												<th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
													→
												</th>
												<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
													Map To
												</th>
												<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
													Action
												</th>
											</tr>
										</thead>
										<tbody className="bg-white divide-y divide-gray-200">
											{preview.mappings.map((mapping, index) => {
												const lineItemId = mapping.toLineItem
												const isEditing = editingField === lineItemId
												const hasFormula = !!customFormulas[lineItemId]

												return (
													<tr key={index}>
														<td className="px-4 py-2 text-sm text-gray-900">{mapping.fromFile}</td>
														<td className="px-4 py-2 text-center text-gray-400">→</td>
														<td className="px-4 py-2 text-sm text-gray-900">{mapping.toLineItem}</td>
														<td className="px-4 py-2 text-sm">
															{isEditing ? (
																// Edit Mode: Show input box with save/cancel buttons
																<div className="flex items-center space-x-2">
																	<input
																		type="text"
																		value={formulaInput}
																		onChange={(e) => setFormulaInput(e.target.value)}
																		onKeyDown={(e) => {
																			if (e.key === 'Enter') {
																				handleSaveFormula(lineItemId)
																			} else if (e.key === 'Escape') {
																				handleCancelEditFormula()
																			}
																		}}
																		placeholder="e.g., Sales - COGS"
																		className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
																		autoFocus
																	/>
																	<button
																		onClick={() => handleSaveFormula(lineItemId)}
																		className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
																		title="Save formula"
																	>
																		✓
																	</button>
																	<button
																		onClick={handleCancelEditFormula}
																		className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
																		title="Cancel"
																	>
																		✕
																	</button>
																</div>
															) : hasFormula ? (
																// Display Mode with Formula: Show formula with calculator icon (clickable)
																<button
																	onClick={() => handleStartEditFormula(lineItemId)}
																	className="flex items-center space-x-1 text-purple-600 hover:text-purple-700 hover:underline"
																>
																	<span className="text-sm">🧮</span>
																	<span className="font-mono text-xs">{customFormulas[lineItemId]}</span>
																</button>
															) : (
																// Default Mode: Show "Update Existing" or "Create new" (clickable)
																<button
																	onClick={() => handleStartEditFormula(lineItemId)}
																	className={`${
																		mapping.action === 'update'
																			? 'text-green-600 hover:text-green-700'
																			: 'text-blue-600 hover:text-blue-700'
																	} hover:underline`}
																>
																	{mapping.action === 'update' ? '✓ Update existing' : '⊕ Create new'}
																</button>
															)}
														</td>
													</tr>
												)
											})}
										</tbody>
									</table>
								</div>
							</div>

							<div className="mt-4 space-y-2">
								<div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
									<p className="text-sm text-yellow-800">
										⚠ <strong>Note:</strong>{' '}
										{preview.mappings.filter(m => m.action === 'create').length} new line items will be created.{' '}
										{importMode === 'replace' ? 'All existing data will be replaced.' : 'Existing data will be updated.'}
									</p>
								</div>

								{Object.keys(customFormulas).length > 0 && (
									<div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
										<p className="text-sm text-purple-800">
											🧮 <strong>Calculated Fields:</strong>{' '}
											{Object.keys(customFormulas).length} field(s) will be calculated using custom formulas.
											Values from the file will be replaced with calculated results.
										</p>
									</div>
								)}
							</div>
						</div>
					)}
				</div>

				{preview && (
					<DialogFooter>
						<Button variant="ghost" onClick={handleCancel}>
							Cancel
						</Button>
						<Button variant="primary" onClick={handleConfirmImport} disabled={isImporting}>
							{isImporting ? 'Importing...' : 'Confirm Import'}
						</Button>
					</DialogFooter>
				)}
			</DialogContent>
		</Dialog>
	)
}
